import React, { useState, useEffect } from 'react';
import AddressAutocomplete from './common/AddressAutocomplete';
import localeConfig from '../config/locale.config';

const EditLocationRow = ({
    location,
    onCancelEdit,
    onSaveEdit,
    loading,
    formatMobileNumber
}) => {

    
    // Local state for the edit form
    const [editForm, setEditForm] = useState({
        location_address: {
            address: '',
            city: '',
            state: '',
            pincode: '',
            latitude: null,
            longitude: null
        },
        operating_hours: {
            monday: { open: '06:00', close: '18:00' },
            tuesday: { open: '06:00', close: '18:00' },
            wednesday: { open: '06:00', close: '18:00' },
            thursday: { open: '06:00', close: '18:00' },
            friday: { open: '06:00', close: '18:00' },
            saturday: { open: '07:00', close: '16:00' },
            sunday: { open: '08:00', close: '14:00' }
        },
        vendor_location_type: 'VENDOR_ONLY',
        manager_name: '',
        manager_mobile: '',
        manager_email: ''
    });

    // Initialize form with existing data when component mounts
    useEffect(() => {
        if (location) {
            setEditForm({
                location_address: {
                    address: location.location_address?.address || '',
                    city: location.location_address?.city || '',
                    state: location.location_address?.state || '',
                    pincode: location.location_address?.pincode || '',
                    latitude: location.location_address?.latitude || null,
                    longitude: location.location_address?.longitude || null
                },
                operating_hours: location.operating_hours || {
                    monday: { open: '06:00', close: '18:00' },
                    tuesday: { open: '06:00', close: '18:00' },
                    wednesday: { open: '06:00', close: '18:00' },
                    thursday: { open: '06:00', close: '18:00' },
                    friday: { open: '06:00', close: '18:00' },
                    saturday: { open: '07:00', close: '16:00' },
                    sunday: { open: '08:00', close: '14:00' }
                },
                vendor_location_type: location.vendor_location_type || 'VENDOR_ONLY',
                manager_name: location.manager?.name || '',
                manager_mobile: location.manager?.mobile_number || '',
                manager_email: location.manager?.email || ''
            });
        }
    }, [location]);

    const handleFormChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setEditForm(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setEditForm(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleAddressChange = (value) => {
        setEditForm(prev => ({
            ...prev,
            location_address: {
                ...prev.location_address,
                address: value
            }
        }));
    };

    const handleAddressSelect = (addressData) => {
        setEditForm(prev => ({
            ...prev,
            location_address: {
                address: addressData.address,
                state: addressData.state,
                city: addressData.city,
                latitude: addressData.latitude,
                longitude: addressData.longitude,
                pincode: addressData.pincode
            }
        }));
    };

    const handleOperatingHoursChange = (day, field, value) => {
        setEditForm(prev => ({
            ...prev,
            operating_hours: {
                ...prev.operating_hours,
                [day]: {
                    ...prev.operating_hours[day],
                    [field]: value
                }
            }
        }));
    };

    const handleSubmit = () => {
        // Detect which fields have actually been changed
        const changedFields = {};
        
        // Check location_address fields
        if (editForm.location_address.address !== (location.location_address?.address || '')) {
            changedFields.location_address = {
                address: editForm.location_address.address,
                city: editForm.location_address.city,
                state: editForm.location_address.state,
                pincode: editForm.location_address.pincode,
                latitude: editForm.location_address.latitude,
                longitude: editForm.location_address.longitude
            };
        } else if (editForm.location_address.city !== (location.location_address?.city || '') ||
                   editForm.location_address.state !== (location.location_address?.state || '') ||
                   editForm.location_address.pincode !== (location.location_address?.pincode || '') ||
                   editForm.location_address.latitude !== (location.location_address?.latitude || null) ||
                   editForm.location_address.longitude !== (location.location_address?.longitude || null)) {
            // If any other address field changed, include the full address
            changedFields.location_address = {
                address: editForm.location_address.address,
                city: editForm.location_address.city,
                state: editForm.location_address.state,
                pincode: editForm.location_address.pincode,
                latitude: editForm.location_address.latitude,
                longitude: editForm.location_address.longitude
            };
        }
        
        // Check operating hours - if any day changed, include all days
        const originalHours = location.operating_hours || {};
        const hasHoursChanged = Object.keys(editForm.operating_hours).some(day => {
            const original = originalHours[day] || {};
            const edited = editForm.operating_hours[day] || {};
            return original.open !== edited.open || original.close !== edited.close;
        });
        
        if (hasHoursChanged) {
            changedFields.operating_hours = editForm.operating_hours;
        }
        
        // Check other fields
        if (editForm.vendor_location_type !== (location.vendor_location_type || 'VENDOR_ONLY')) {
            changedFields.vendor_location_type = editForm.vendor_location_type;
        }
        
        if (editForm.manager_name !== (location.manager?.name || '')) {
            changedFields.manager_name = editForm.manager_name;
        }
        
        if (editForm.manager_mobile !== (location.manager?.mobile_number || '')) {
            changedFields.manager_mobile = editForm.manager_mobile;
        }
        
        if (editForm.manager_email !== (location.manager?.email || '')) {
            changedFields.manager_email = editForm.manager_email;
        }
        
        // Only submit if there are actual changes
        if (Object.keys(changedFields).length === 0) {
            alert('No changes detected. Please make changes before saving.');
            return;
        }
        
        console.log('Submitting only changed fields:', changedFields);
        onSaveEdit(changedFields);
    };

    return (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
            <div className="space-y-6">
                {/* Current Data Display (Read-only) */}
                <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-3">Current Location Data (Read-only)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <strong>Address:</strong> {location.location_address?.address || 'Not set'}
                        </div>
                        <div>
                            <strong>City:</strong> {location.location_address?.city || 'Not set'}
                        </div>
                        <div>
                            <strong>{localeConfig.address.regionLabel}:</strong> {location.location_address?.state || 'Not set'}
                        </div>
                        <div>
                            <strong>Manager:</strong> {location.manager?.name || 'Not set'}
                        </div>
                        <div>
                            <strong>Mobile:</strong> {location.manager?.mobile_number ? location.manager.mobile_number.slice(-10) : 'Not set'}
                        </div>
                        <div>
                            <strong>Type:</strong> {location.vendor_location_type || 'Not set'}
                        </div>
                    </div>
                </div>

                {/* Edit Form */}
                <div className="bg-white p-4 rounded-md border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">Edit Location Data</h4>
                    
                    {/* Change Summary */}
                    {(() => {
                        const changedFields = [];
                        if (editForm.location_address.address !== (location.location_address?.address || '')) changedFields.push('Address');
                        if (editForm.location_address.city !== (location.location_address?.city || '')) changedFields.push('City');
                        if (editForm.location_address.state !== (location.location_address?.state || '')) changedFields.push(localeConfig.address.regionLabel);
                        if (editForm.location_address.pincode !== (location.location_address?.pincode || '')) changedFields.push(localeConfig.address.postcodeLabel);
                        if (editForm.manager_name !== (location.manager?.name || '')) changedFields.push('Manager Name');
                        if (editForm.manager_mobile !== (location.manager?.mobile_number || '')) changedFields.push('Manager Mobile');
                        if (editForm.manager_email !== (location.manager?.email || '')) changedFields.push('Manager Email');
                        if (editForm.vendor_location_type !== (location.vendor_location_type || 'VENDOR_ONLY')) changedFields.push('Location Type');
                        
                        const originalHours = location.operating_hours || {};
                        const hasHoursChanged = Object.keys(editForm.operating_hours).some(day => {
                            const original = originalHours[day] || {};
                            const edited = editForm.operating_hours[day] || {};
                            return original.open !== edited.open || original.close !== edited.close;
                        });
                        if (hasHoursChanged) changedFields.push('Operating Hours');
                        
                        if (changedFields.length > 0) {
                            return (
                                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                                    <div className="text-sm text-blue-800">
                                        <strong>Fields to be updated:</strong> {changedFields.join(', ')}
                                    </div>
                                </div>
                            );
                        }
                        return null;
                    })()}
                    
                    {/* Address Section */}
                    <div className="mb-6">
                        <h5 className="font-medium text-gray-700 mb-2">Location Address</h5>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address {editForm.location_address.address ? '(Modified)' : ''}
                                </label>
                                <AddressAutocomplete
                                    value={editForm.location_address.address}
                                    onChange={handleAddressChange}
                                    onAddressSelect={handleAddressSelect}
                                    placeholder="Enter new address (leave empty to keep current)"
                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                    City {editForm.location_address.city !== (location.location_address?.city || '') ? '(Modified)' : ''}
                                </label>
                                    <input
                                        type="text"
                                        value={editForm.location_address.city}
                                        onChange={(e) => handleFormChange('location_address.city', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter new city"
                                    />
                                </div>
                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {localeConfig.address.regionLabel} {editForm.location_address.state !== (location.location_address?.state || '') ? '(Modified)' : ''}
                                </label>
                                    <input
                                        type="text"
                                        value={editForm.location_address.state}
                                        onChange={(e) => handleFormChange('location_address.state', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={`Enter new ${localeConfig.address.regionLabel.toLowerCase()}`}
                                    />
                                </div>
                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {localeConfig.address.postcodeLabel} {editForm.location_address.pincode !== (location.location_address?.pincode || '') ? '(Modified)' : ''}
                                </label>
                                    <input
                                        type="text"
                                        value={editForm.location_address.pincode}
                                        onChange={(e) => handleFormChange('location_address.pincode', e.target.value)}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={`Enter new ${localeConfig.address.postcodeLabel.toLowerCase()}`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Manager Details Section */}
                    <div className="mb-6">
                        <h5 className="font-medium text-gray-700 mb-2">Manager Details</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Manager Name {editForm.manager_name !== (location.manager?.name || '') ? '(Modified)' : ''}
                                </label>
                                <input
                                    type="text"
                                    value={editForm.manager_name}
                                    onChange={(e) => handleFormChange('manager_name', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter new manager name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Manager Mobile {editForm.manager_mobile !== (location.manager?.mobile_number || '') ? '(Modified)' : ''}
                                </label>
                                <input
                                    type="tel"
                                    value={editForm.manager_mobile}
                                    onChange={(e) => handleFormChange('manager_mobile', formatMobileNumber(e.target.value))}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter new mobile number"
                                    maxLength={localeConfig.mobile.maxDigits}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Manager Email {editForm.manager_email !== (location.manager?.email || '') ? '(Modified)' : ''}
                                </label>
                                <input
                                    type="email"
                                    value={editForm.manager_email}
                                    onChange={(e) => handleFormChange('manager_email', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter new email"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Location Type Section */}
                    <div className="mb-6">
                        <h5 className="font-medium text-gray-700 mb-2">
                            Location Type {editForm.vendor_location_type !== (location.vendor_location_type || 'VENDOR_ONLY') ? '(Modified)' : ''}
                        </h5>
                        <select
                            value={editForm.vendor_location_type}
                            onChange={(e) => handleFormChange('vendor_location_type', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="VENDOR_ONLY">Vendor Only</option>
                            <option value="VENDOR_AND_DEALER">Vendor & Dealer</option>
                            <option value="DEALER_PARTNER">Dealer Partner</option>
                        </select>
                    </div>

                    {/* Operating Hours Section */}
                    <div className="mb-6">
                        <h5 className="font-medium text-gray-700 mb-2">
                            Operating Hours {(() => {
                                const originalHours = location.operating_hours || {};
                                const hasHoursChanged = Object.keys(editForm.operating_hours).some(day => {
                                    const original = originalHours[day] || {};
                                    const edited = editForm.operating_hours[day] || {};
                                    return original.open !== edited.open || original.close !== edited.close;
                                });
                                return hasHoursChanged ? '(Modified)' : '';
                            })()}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {Object.entries(editForm.operating_hours).map(([day, hours]) => (
                                <div key={day} className="bg-gray-50 p-3 rounded-md">
                                    <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                                        {day}
                                    </label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Open</label>
                                            <input
                                                type="time"
                                                value={hours.open}
                                                onChange={(e) => handleOperatingHoursChange(day, 'open', e.target.value)}
                                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-600 mb-1">Close</label>
                                            <input
                                                type="time"
                                                value={hours.close}
                                                onChange={(e) => handleOperatingHoursChange(day, 'close', e.target.value)}
                                                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                            onClick={onCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditLocationRow; 